import { motion } from 'framer-motion';
import { microInteractions } from '../../utils/animations';
import { haptics } from '../../utils/haptics';

/**
 * Modern Button Component with Spring Physics
 * Features press animations, ripple effects, and haptic feedback
 */
export default function Button({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  type = 'button',
  className = '',
  ...props
}) {
  const handleClick = (e) => {
    if (!disabled && !loading) {
      haptics.light();
      onClick?.(e);
    }
  };

  const baseClasses = 'inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 touch-target';
  
  const variantClasses = {
    primary: 'bg-gradient-primary text-white shadow-soft hover:shadow-glow',
    secondary: 'bg-gray-200 dark:bg-white/10 dark:backdrop-blur-sm dark:border dark:border-white/15 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-white/20 dark:hover:border-white/25',
    ghost: 'hover:bg-gray-100 dark:hover:bg-elevated-dark-2 text-gray-700 dark:text-gray-300',
    danger: 'bg-red-600 text-white hover:bg-red-700 shadow-soft',
    success: 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-soft',
  };
  
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-body-sm squircle-sm',
    md: 'px-4 py-2 text-body-sm squircle-sm',
    lg: 'px-6 py-3 text-body squircle',
  };
  
  return (
    <motion.button
      type={type}
      onClick={handleClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...microInteractions.buttonPress}
      {...props}
    >
      {loading && (
        <svg 
          className="animate-spin -ml-1 mr-2 h-4 w-4" 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24"
        >
          <circle 
            className="opacity-25" 
            cx="12" 
            cy="12" 
            r="10" 
            stroke="currentColor" 
            strokeWidth="4"
          />
          <path 
            className="opacity-75" 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </motion.button>
  );
}
